import { useMemo } from "react";
import {
  CaretDown,
  CaretUp,
  Chat,
  ExclamationTriangle,
} from "@reverb-ui/icons-react";
import {
  Box,
  Flex,
  Heading,
  HStack,
  SkeletonCircle,
  SkeletonText,
  Text,
  Tooltip,
} from "@reverb-ui/react";
import {
  IconVariationEnum,
  InsightsFeedbackColor,
  InsightsFeedbackEnum,
} from "data/constants/constants";
import { findCard } from "helper/cardsHelper";
import { HeaderCard } from "./HeaderCard";
import EqualRondedIcon from "Icons/EqualRonded";

export type TMetricCardProps = {
  order: number;
  iconName: string;
  title?: string;
  description?: string;
  variation?: string;
  variationPeriod?: string;
  variationType?: IconVariationEnum;
  negativeMetric?: boolean;
  subDescription?: string;
  upDescription?: string;
  isLoading?: boolean;
  insight?: TInsightFeedbackProps;
};

export const InsightsFeedbackIcon = {
  [InsightsFeedbackEnum.POSITIVE]: <ExclamationTriangle color="green.500" />,
  [InsightsFeedbackEnum.NEGATIVE]: <ExclamationTriangle color="red.500" />,
  [InsightsFeedbackEnum.NEUTRAL]: <Chat color="gray.500" />,
};

type TInsightFeedbackProps = {
  feedback: InsightsFeedbackEnum;
  message: string;
  icon?: React.ReactNode;
};

const InsightFeedback = ({
  feedback,
  message,
  icon,
}: TInsightFeedbackProps) => {
  return (
    <>
      {message && (
        <Box pt={2} w="full">
          <Flex bg={InsightsFeedbackColor[feedback]} borderRadius="3px">
            <Box minW="14px" mt="11px" ml="10px">
              {icon || InsightsFeedbackIcon[feedback]}
            </Box>
            <Text fontSize="xs" m="10px" color="gray.800">
              {message}
            </Text>
          </Flex>
        </Box>
      )}
    </>
  );
};

export const MetricCard = ({
  order,
  iconName,
  title,
  description,
  variation,
  variationPeriod,
  variationType,
  negativeMetric,
  subDescription,
  upDescription,
  insight,
  isLoading = false,
}: TMetricCardProps) => {
  const cardInfo = findCard(order);

  const variationIcon = useMemo(() => {
    if (variationType === IconVariationEnum.DOWN) {
      const color = negativeMetric ? "green.500" : "red.500";
      return <CaretDown color={color} variant="solid" />;
    } else if (variationType === IconVariationEnum.EQUAL) {
      return <EqualRondedIcon />;
    }

    const color = negativeMetric ? "red.500" : "green.500";
    return <CaretUp color={color} variant="solid" />;
  }, [negativeMetric, variationType]);

  return (
    <Box p={6} bg="white" w="full">
      <Tooltip
        bg="gray.100"
        color="gray.800"
        label={cardInfo?.tooltipMessage}
        isDisabled={!cardInfo?.tooltipMessage}
      >
        <Box>
          <HeaderCard title={title} iconName={iconName} />
          {upDescription && (
            <SkeletonText isLoaded={!isLoading} variant="rect" noOfLines={1}>
              <Text color="gray.800" fontWeight="bold" fontSize="sm">
                {upDescription}
              </Text>
            </SkeletonText>
          )}
          <SkeletonText
            isLoaded={!isLoading}
            variant="rect"
            noOfLines={1}
            skeletonHeight={"1.5rem"}
          >
            <Heading color="gray.800" fontWeight="bold" fontSize="xl">
              {description}
            </Heading>
          </SkeletonText>
          {subDescription && (
            <SkeletonText
              isLoaded={!isLoading}
              variant="rect"
              paddingTop="5px"
              noOfLines={1}
            >
              <Text color="gray.800" fontWeight="bold" fontSize="sm">
                {subDescription}
              </Text>
            </SkeletonText>
          )}
          {(variation || variationPeriod) && (
            <HStack pt={6} alignItems="top">
              <SkeletonCircle
                isLoaded={!isLoading}
                variant="rect"
                width={3}
                height={3}
              >
                {variationType && variationIcon}
              </SkeletonCircle>
              <SkeletonText
                isLoaded={!isLoading}
                variant="rect"
                noOfLines={1}
                skeletonHeight={"1rem"}
              >
                <Text fontWeight="bold" fontSize="xs" color="gray.800">
                  {variation}
                </Text>
              </SkeletonText>
              <SkeletonText isLoaded={!isLoading} variant="rect" noOfLines={2}>
                <Text fontSize="xs" color="gray.600">
                  {variationPeriod}
                </Text>
              </SkeletonText>
            </HStack>
          )}
          <HStack>
            {insight && (
              <InsightFeedback
                feedback={insight.feedback}
                message={insight.message}
                icon={insight.icon}
              />
            )}
          </HStack>
        </Box>
      </Tooltip>
    </Box>
  );
};
